import axios from "axios";
import { pageLang } from "../App";
import { languageMap, requestERPLYData } from "../utils";

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (
      error.request.responseType === "blob" &&
      error.response.data instanceof Blob &&
      error.response.data.type &&
      error.response.data.type.toLowerCase().indexOf("json") !== -1
    ) {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
          error.response.data = JSON.parse(reader.result);
          resolve(Promise.reject(error));
        };

        reader.onerror = () => {
          reject(error);
        };

        reader.readAsText(error.response.data);
      });
    }

    return Promise.reject(error);
  }
);

let headers = {
  clientCode: "",
  sessionKey: "",
  baseUrl: "",
};

const Server = {
  init: (config) => {
    headers = {
      clientCode: config.keys.clientCode,
      sessionKey: config.keys.sessionKey,
      baseUrl: config.baseUrl,
    };
  },
  getProducts: async (params, baseURL, keys) => {
    return await axios
      .get(baseURL + "v1/product" + params, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        const {
          response: { data },
        } = error;
        console.log(data.message);
        return [];
      });
  },

  getProductsNew: async (params, baseURL, keys) => {
    return await axios.get(
      baseURL + "v1/ui/product" + params + "&withTotalCount=true",
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getProductsInBatch: async (ids, params, baseURL, keys) => {
    return await axios
      .get(baseURL + "v1/product/" + ids + params, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return [];
      });
  },

  getProductCount: async (baseURL, keys) => {
    const url = baseURL + "v1/product/count";
    return await axios
      .get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },
  insertProduct: async (values, baseURL, keys, autoCode, isCode2Auto) => {
    return await axios.post(baseURL + "v1/product", values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: {
        generateCodeAutomatically: autoCode,
        generateCode2Automatically: isCode2Auto,
        trimInput: true,
      },
    });
  },

  updateProduct: async (id, values, baseURL, keys) => {
    return await axios.put(baseURL + "v1/product/" + id, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { trimInput: true },
    });
  },

  updateProductType: async (id, value, baseURL, keys) => {
    const url = `v1/product/${id}`;
    return await axios.patch(baseURL + url, value, {
      headers: {
        clientCode: keys.clientCode,
        sessionKey: keys.sessionKey,
      },
    });
  },

  updateProductFromForm: async (id, values, baseURL, keys) => {
    return await axios.put(baseURL + "v1/product/" + id, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  removeProduct: async (id, baseURL, keys) => {
    return await axios
      .delete(baseURL + "v1/product/" + id, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .catch((error) => {
        return [];
      });
  },
  getProductGroups: async (baseURL, keys) => {
    const url = baseURL + "v1/product/group";
    return await axios
      .get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  getProductGroupsIndented: async (baseURL, keys) => {
    const url = baseURL + "v1/product/group/indented";
    let isoCode = "";

    if (pageLang && languageMap[pageLang]) {
      isoCode = languageMap[pageLang];
    }

    if (isoCode && isoCode.length === 2) {
      return await axios.get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { lang: isoCode },
      });
    }
    return await axios.get(url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductGroupCount: async (baseURL, keys) => {
    const url = baseURL + "v1/product/group/count";
    return await axios
      .get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  saveProductGroup: async (values, baseURL, keys) => {
    const url = baseURL + "v1/product/group";
    return await axios.post(
      url,
      {
        name: values.name,
        main_id: values.parent_id,
        non_discountable: values.non_discountable,
        show_in_webshop: values.show_in_webshop,
      },
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  updateProductGroup: async (id, values, baseURL, keys) => {
    const url = baseURL + "v1/product/group/";
    return await axios.put(url + id, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  removeProductGroup: async (id, baseURL, keys) => {
    const url = baseURL + "v1/product/group/";
    return await axios.delete(url + id, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  moveProductGroup: async (id, targetId, position, baseURL, keys) => {
    const url = baseURL + "v1/product/group/";
    return await axios.put(
      url + id + "/move",
      {
        position: position,
        target_id: targetId,
      },
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getProductFamilies: async (baseURL, keys) => {
    return await axios.get(baseURL + "v1/product/family", {
      params: {
        withArchived: true,
        skip: 0,
        take: 100000,
      },
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  saveProductFamily: async (values, baseURL, keys) => {
    return await axios.post(baseURL + "v1/product/family", values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateProductFamily: async (id, values, baseURL, keys) => {
    return await axios.put(baseURL + "v1/product/family/" + id, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductCategories: async (baseURL, keys) => {
    const url = baseURL + "v1/product/category";
    return await axios
      .get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  getProductCategoryIndented: async (baseURL, keys) => {
    const url = `${baseURL}v1/product/category/indented`;
    let isoCode = "";

    if (pageLang && languageMap[pageLang]) {
      isoCode = languageMap[pageLang];
    }

    if (isoCode && isoCode.length === 2) {
      return await axios.get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { lang: isoCode },
      });
    }
    return await axios.get(url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateProductCategory: async (id, values, baseURL, keys) => {
    const url = baseURL + "v1/product/category/";
    return await axios.put(url + id, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  insertProductCategory: async (values, baseURL, keys) => {
    const url = baseURL + "v1/product/category";
    return await axios.post(
      url,
      { name: values.name, main_id: values.parent_id },
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },
  removeCategory: async (id, baseURL, keys) => {
    const url = baseURL + "v1/product/category/";
    return await axios.delete(url + id, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },
  moveProductCategory: async (id, targetId, position, baseURL, keys) => {
    const url = baseURL + "v1/product/category/";
    return await axios.put(
      url + id + "/move",
      {
        position: position,
        target_id: targetId,
      },
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getProductBrands: async (baseURL, keys) => {
    return await axios
      .get(baseURL + "v1/brand", {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  createBrand: async (value, baseURL, keys) => {
    return await axios.post(baseURL + "v1/brand", value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateBrand: async (id, value, baseURL, keys) => {
    return await axios.put(baseURL + `v1/brand/${id}`, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteBrand: async (id, baseURL, keys) => {
    return await axios.delete(baseURL + `v1/brand/${id}`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getSuppliers: async (baseURL, keys, params, withTotalCount) => {
    let url = "v1/supplier";
    if (params) {
      url = `v1/supplier${params}`;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { withTotalCount: withTotalCount, skip: 0, take: 100000 },
    });
  },

  getSupplierTypes: async (baseURL, keys) => {
    return await axios.get(baseURL + "v1/supplier/type", {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createSupplier: async (values, baseURL, keys) => {
    const url = "v1/supplier";
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateSupplier: async (id, values, baseURL, keys) => {
    const url = `v1/supplier/${id}`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteSupplier: async (id, baseURL, keys) => {
    const url = `v1/supplier/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductParameters: async (baseURL, keys, params) => {
    let url = baseURL + "v1/product/parameter?withOptions=1";
    if (params) {
      url = baseURL + "v1/product/parameter" + params;
    }
    return await axios
      .get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  updateProductParameters: async (id, name, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/";
    return await axios.put(url + id, name, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteProductParameter: async (id, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/";
    return await axios.delete(url + id, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  saveProductParameter: async (values, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter";
    return await axios.post(url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },
  getParameterSet: async (baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/set";
    return await axios
      .get(url, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  saveParameterSet: async (name, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/set";
    return await axios.post(url, name, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateParameterSet: async (id, name, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/set/";
    return await axios.put(url + id, name, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteParameterSet: async (id, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/set/";
    return await axios
      .delete(url + id, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  saveProductParameterOption: async (values, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/option";
    return await axios.post(url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductParameterOption: async (id, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/option/";
    return await axios.get(url + id, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  updateParameterOption: async (id, name, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/option/";
    return await axios.put(url + id, name, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  removeParameterOption: async (id, baseURL, keys) => {
    const url = baseURL + "v1/product/parameter/option/";
    return await axios.delete(url + id, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getAssortments: async (baseURL, keys) => {
    return await axios
      .get(baseURL + "v1/assortment", {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  createAssortment: async (values, baseURL, keys) => {
    return await axios
      .post(
        baseURL + "v1/assortment",
        {
          code: values.code,
          name: values.name,
        },
        {
          headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        }
      )
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  updateAssortment: async (request, baseURL, keys) => {
    return await axios
      .put(baseURL + "v1/assortment/" + request.id, request.values, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  addProductToAssortment: async (id, values, baseURL, keys) => {
    return await axios.post(
      baseURL + "v1/assortment/" + id + "/products/bulk",
      values,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getAssortmentProduct: async (id, baseURL, keys, params) => {
    return await axios.get(
      baseURL + "v1/assortment/" + id + "/products/data" + params,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { withTotalCount: true },
      }
    );
  },

  getAssortmentProductWithoutData: async (id, baseURL, keys) => {
    return await axios
      .get(baseURL + "v1/assortment/" + id + "/products", {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        // console.log(response);

        return response.data;
      })
      .catch((error) => {
        const {
          response: { data },
        } = error;
        return [];
      });
  },

  deleteAssortment: async (id, baseURL, keys) => {
    return await axios
      .delete(baseURL + "v1/assortment/" + id, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  deleteAssortmentProductsSingle: async (id, baseURL, keys) => {
    return await axios.delete(baseURL + "v1/assortment/products/" + id, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteAssortmentProducts: async (ids, baseURL, keys) => {
    return await axios.delete(
      baseURL + "v1/assortment/products/" + ids + "/bulk",
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getProductAssortments: async (id, baseURL, keys) => {
    return await axios
      .get(baseURL + "v1/product/" + id + "/assortments", {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { skip: 0, take: 100000 },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return { err: error.isAxiosError };
      });
  },

  updateProductAssortmentStatus: async (linkId, value, baseURL, keys) => {
    const url = `v1/assortment/products/${linkId}`;
    return await axios.patch(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  linkParameterSetToProductGroup: async (value, baseURL, keys) => {
    const url = `v1/product/parameter/set/${value.set_id}/groups`;
    return await axios.post(
      baseURL + url,
      { id: value.groupId },
      { headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey } }
    );
  },

  getParameterSetGroupLink: async (setId, baseURL, keys) => {
    const url = `v1/product/parameter/set/${setId}/groups`;
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  deleteParameterSetLink: async (linkId, baseURL, keys) => {
    const url = `v1/product/parameter/set/groups/${linkId}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getParameterForProduct: async (id, baseURL, keys) => {
    return await axios.get(baseURL + "v1/product/" + id + "/parameters", {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  saveParameterValue: async (productId, parameterId, value, baseURL, keys) => {
    const url = `v1/product/${productId}/parameter/${parameterId}/value`;

    return await axios.post(baseURL + url, value, {
      headers: {
        clientCode: keys.clientCode,
        sessionKey: keys.sessionKey,
        "Apply-Validation-Rules": false,
      },
    });
  },

  updateParameterValue: async (valueId, value, baseURL, keys) => {
    const url = `v1/product/parameter/value/${valueId}`;

    return await axios.put(baseURL + url, value, {
      headers: {
        clientCode: keys.clientCode,
        sessionKey: keys.sessionKey,
        "Apply-Validation-Rules": false,
      },
    });
  },

  deleteParameterValue: async (valueId, baseURL, keys) => {
    const url = `v1/product/parameter/value/${valueId}`;
    return await axios.delete(baseURL + url, {
      headers: {
        clientCode: keys.clientCode,
        sessionKey: keys.sessionKey,
        "Apply-Validation-Rules": false,
      },
    });
  },

  getParameterValues: async (productId, baseURL, keys) => {
    const url = `v1/product/${productId}/parameter/value`;
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },
  getJSONObject: async (baseUrl, productId, keys) => {
    const url = `v1/json_object/product/${productId}`;
    return await axios.get(baseUrl + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },
  createJSONObject: async (baseUrl, JSONObject, productId, keys) => {
    const url = `v1/json_object/product/${productId}`;
    return await axios.put(
      baseUrl + url,
      { json_object: JSONObject },
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },
  getProductPackageCategory: async (baseURL, keys) => {
    const url = "v1/product/package/category";
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  getProductPackage: async (baseURL, keys, params) => {
    const url = "v1/product/package";
    return await axios.get(baseURL + url + params, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createProductPackage: async (value, baseURL, keys) => {
    const url = "v1/product/package";
    return await axios.post(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateProductPackage: async (id, value, baseURL, keys) => {
    const url = `v1/product/package/${id}`;
    return await axios.put(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteProductPackage: async (id, baseURL, keys) => {
    const url = `v1/product/package/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getUnits: async (baseURL, keys) => {
    const url = "v1/product/unit";
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createUnits: async (values, baseURL, keys) => {
    const url = "v1/product/unit";
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateUnit: async (id, values, baseURL, keys) => {
    const url = `v1/product/unit/${id}`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteUnit: async (id, baseURL, keys) => {
    const url = `v1/product/unit/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  moveUnit: async (id, values, baseURL, keys) => {
    const url = `v1/product/unit/${id}/move`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getMatrixDimensions: async (baseURL, keys, params) => {
    let url = "v1/matrix/dimension";
    if (params) {
      url = "v1/matrix/dimension" + params;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  getMatrixDimensionsValues: async (params, baseURL, keys) => {
    let url = "v1/matrix/dimension/value";
    if (params) {
      url = "v1/matrix/dimension/value" + params;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { withTotalCount: true },
    });
  },

  getMatrixDimensionsValuesBulk: async (params, baseURL, keys) => {
    let url = "v1/matrix/dimension/value";
    if (params) {
      url = "v1/matrix/dimension/value" + params;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createDimension: async (value, baseURL, keys) => {
    const url = "v1/matrix/dimension";
    return await axios.post(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },
  updateDimension: async (id, value, baseURL, keys) => {
    const url = `v1/matrix/dimension/${id}`;
    return await axios.put(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  moveDimension: async (id, value, baseURL, keys) => {
    const url = `v1/matrix/dimension/${id}/move`;
    return await axios.put(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteDimension: async (id, baseURL, keys) => {
    const url = `v1/matrix/dimension/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  createDimensionValue: async (value, baseURL, keys) => {
    const url = "v1/matrix/dimension/value";
    return await axios.post(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateDimensionValue: async (id, value, baseURL, keys) => {
    const url = `v1/matrix/dimension/value/${id}`;
    return await axios.put(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteDimensionValue: async (id, baseURL, keys) => {
    const url = `v1/matrix/dimension/value/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  moveDimensionValue: async (id, value, baseURL, keys) => {
    const url = `v1/matrix/dimension/value/${id}/move`;
    return await axios.put(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductCountries: async (baseURL, keys) => {
    const url = "v1/product/country";
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createCountry: async (values, baseURL, keys) => {
    const url = "v1/product/country";
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateCountry: async (id, values, baseURL, keys) => {
    const url = `v1/product/country/${id}`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteCountry: async (id, baseURL, keys) => {
    const url = `v1/product/country/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getPriorityGroup: async (baseURL, keys) => {
    const url = "v1/product/priority/group";
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createPriorityGroup: async (values, baseURL, keys) => {
    const url = "v1/product/priority/group";
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updatePriorityGroup: async (id, values, baseURL, keys) => {
    const url = `v1/product/priority/group/${id}`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deletePriorityGroup: async (id, baseURL, keys) => {
    const url = `v1/product/priority/group/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  movePriorityGroup: async (sourceId, values, baseURL, keys) => {
    const url = `v1/product/priority/group/${sourceId}/move`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getAdditionalGroups: async (id, baseURL, keys) => {
    const url = `v1/product/${id}/additional-groups`;
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  saveAdditionalGroups: async (value, id, baseURL, keys) => {
    const url = `v1/product/${id}/additional-groups`;
    return await axios.put(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  searchNameIncrementally: async (requestBody) => {
    return await requestERPLYData(requestBody);
  },

  getLinkedProducts: async (id, type, baseURL, keys) => {
    const url = `v1/product/${id}/linked-products`;
    return await axios.get(baseURL + url + type, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  linkProduct: async (value, baseURL, keys) => {
    const url = "v1/product/linked-products";
    return await axios.post(baseURL + url, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteLink: async (linkId, baseURL, keys) => {
    const url = `v1/product/linked-products/${linkId}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  saveMatrixProductDimensions: async (productId, values, baseURL, keys) => {
    const url = `v1/matrix/product/${productId}/dimensions`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getMatrixProductDimensions: async (productId, baseURL, keys) => {
    const url = `v1/matrix/product/${productId}/dimensions`;
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  getMatrixProducts: async (productId, baseURL, keys, params) => {
    const url = `v1/matrix/product?${params}=${productId}`;
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createVariationProducts: async (productId, values, baseURL, keys) => {
    const url = `v1/matrix/product/${productId}/variation`;
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductRecipes: async (baseURL, keys, params) => {
    let url = `v1/product/recipe`;
    if (params) {
      url = `v1/product/recipe${params}`;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  saveProductRecipe: async (values, baseURL, keys) => {
    const url = "v1/product/recipe";
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteProductRecipe: async (id, baseURL, keys) => {
    const url = `v1/product/recipe/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getAttributes: async (baseURL, keys, params) => {
    let url = `v1/attribute`;
    if (params) {
      url = `v1/attribute${params}`;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  createAndUpdateAttribute: async (values, baseURL, keys) => {
    const url = `v1/attribute`;
    return await axios.put(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteAttribute: async (id, baseURL, keys) => {
    const url = `v1/attribute/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductExtraField: async (fieldType, baseURL, keys, params) => {
    let url = `v1/product/extra/field-${fieldType}`;
    if (params) {
      url = `v1/product/extra/field-${fieldType}${params}`;
    }
    return await axios.get(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { skip: 0, take: 100000 },
    });
  },

  addExtraFieldItem: async (fieldType, values, baseURL, keys) => {
    const url = `v1/product/extra/field-${fieldType}`;
    return await axios.post(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateExtraFieldItem: async (fieldType, id, values, baseURL, keys) => {
    const url = `v1/product/extra/field-${fieldType}/${id}`;
    return await axios.patch(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  deleteExtraFieldItem: async (fieldType, id, baseURL, keys) => {
    const url = `v1/product/extra/field-${fieldType}/${id}`;
    return await axios.delete(baseURL + url, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  moveExtraFieldItem: async (fieldType, id, values, baseURL, keys) => {
    const url = `v1/product/extra/field-${fieldType}/${id}/move`;
    return await axios.patch(baseURL + url, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductConfig: async (params, baseURL, keys) => {
    return await axios.get(baseURL + "configuration", {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: params,
    });
  },

  saveProductConfig: async (values, baseURL, keys) => {
    return await axios.post(baseURL + "configuration", values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateProductConfig: async (values, baseURL, keys) => {
    return await axios.put(baseURL + "configuration", values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateProductInBulk: async (values, baseURL, keys) => {
    return await axios.put(baseURL + "v1/product/bulk", values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductCodes: async (baseURL, keys) => {
    return await axios.get(`${baseURL}v1/product/codes`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateProductCode: async (value, baseURL, keys) => {
    return await axios.patch(`${baseURL}v1/product/codes`, value, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  productGlobalSearch: async (searchText, baseURL, keys) => {
    return await axios.get(`${baseURL}v1/product/full-text-lookup`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { lookupPhrase: searchText, take: 10000 },
    });
  },

  getWarehouses: async (baseURL, keys) => {
    return await axios.get(`${baseURL}v1/warehouse/locations`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { take: 10000, skip: 0 },
    });
  },

  exportProducts: async (baseURL, keys, params) => {
    if (params) {
      return await axios.get(`${baseURL}v1/export/products`, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { ...params },
        responseType: "blob",
      });
    } else {
      return await axios.get(`${baseURL}v1/export/products`, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        responseType: "blob",
      });
    }
  },
  exportMatrixProducts: async (baseURL, keys, params) => {
    if (params) {
      return await axios.get(`${baseURL}v1/export/matrix-products`, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { ...params },
        responseType: "blob",
      });
    } else {
      return await axios.get(`${baseURL}v1/export/matrix-products`, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        responseType: "blob",
      });
    }
  },
  exportAssortment: async (baseURL, keys, params) => {
    if (params) {
      return await axios.get(`${baseURL}v1/export/assortments`, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: params,
        responseType: "blob",
      });
    } else {
      return await axios.get(`${baseURL}v1/export/assortments`, {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        responseType: "blob",
      });
    }
  },

  updateLocationVat: async (value, id, baseUrl, keys) => {
    return await axios.patch(
      `${baseUrl}/v1/product/group/${id}/location-tax`,
      value,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  deleteLocationVat: async (groupId, warehouseId, baseURL, keys) => {
    return await axios.delete(
      `${baseURL}v1/product/group/${groupId}/location-tax/${warehouseId}`,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getVariationSettings: async (baseURL, keys) => {
    return await axios.get(`${baseURL}v1/product/fields/inherited`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  updateVariationSettings: async (baseURL, keys, values) => {
    return await axios.patch(`${baseURL}v1/product/fields/inherited`, values, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },

  getProductFieldSettings: async (baseURL, keys, userGroupID) => {
    return await axios.get(
      `${baseURL ? baseURL : headers.baseUrl}v1/product/fields/permissions`,
      {
        headers: {
          clientCode: keys ? keys.clientCode : headers.clientCode,
          sessionKey: keys ? keys.sessionKey : headers.sessionKey,
        },
        params: { userGroupID },
      }
    );
  },

  updateProductFieldSettings: async (baseURL, keys, values, userGroupID) => {
    return await axios.patch(
      `${baseURL}v1/product/fields/permissions`,
      values,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
        params: { userGroupID },
      }
    );
  },
  getFranchiseInfo: async (baseUrl, keys) => {
    return await axios.get(`${baseUrl}v1/account/franchise-info`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      // withCredentials: true,
    });
  },

  saveReoderAndRestockFranchise: async (keys, values) => {
    return await axios.post(
      `${keys.inventoryBaseUrl}v1/franchise-inventory/copy-reorder-and-restock-levels`,
      values,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  saveReoderAndRestock: async (keys, values) => {
    return await axios.post(
      `${keys.inventoryBaseUrl}v1/inventory/copy-reorder-and-restock-levels`,
      values,
      {
        headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      }
    );
  },

  getReoderAndRestockCount: async (keys, params, isFranchise) => {
    if (isFranchise) {
      return await axios.get(
        `${keys.inventoryBaseUrl}v1/franchise-inventory/copy-reorder-and-restock-levels`,
        {
          headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
          params,
        }
      );
    } else {
      return await axios.get(
        `${keys.inventoryBaseUrl}v1/inventory/copy-reorder-and-restock-levels`,
        {
          headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
          params,
        }
      );
    }
  },
  getWarehousesAccAdmin: async (keys) => {
    return await axios.get(`${keys.adminBaseUrl}v1/warehouse`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
    });
  },
  searchCustomers: async (keys, lookupPhrase) => {
    return await axios.get(`${keys.crmBaseUrl}v1/customers/full-text-lookup`, {
      headers: { clientCode: keys.clientCode, sessionKey: keys.sessionKey },
      params: { lookupPhrase },
    });
  },
};
export default Server;
